import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import { Link2, Loader2, DollarSign, User } from "lucide-react";
import { MarketplaceBadge } from "../MarketplaceLogo";
import {
  detectPlatformFromUrl,
  previewProductUrl,
  submitProductUrlAsAdmin,
  validateProductUrl,
} from "../../lib/urlSubmission";

/**
 * Shared admin link submission form — used on /admin/submit-link, submissions page,
 * and inside the modal wrapper.
 */
export default function AdminLinkSubmitForm({
  onSubmitted,
  redirectOnSuccess = false,
  compact = false,
}) {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [referencePrice, setReferencePrice] = useState("");
  const [profitMarginOverride, setProfitMarginOverride] = useState("");
  const [platform, setPlatform] = useState("other");
  const [title, setTitle] = useState("");
  const [imagesText, setImagesText] = useState("");
  const [category, setCategory] = useState("General");
  const [estimatedWeight, setEstimatedWeight] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatformFromUrl(url));
  }, [url]);

  const resetForm = () => {
    setUrl("");
    setCustomerEmail("");
    setReferencePrice("");
    setProfitMarginOverride("");
    setPlatform("other");
    setTitle("");
    setImagesText("");
    setCategory("General");
    setEstimatedWeight("");
    setPreviewMeta(null);
  };

  const applyPreview = (result) => {
    setPlatform(result.platform || detectPlatformFromUrl(url));
    if (result.title) setTitle(result.title);
    if (result.images?.length) setImagesText(result.images.join("\n"));
    if (result.category) setCategory(result.category);
    if (result.estimated_weight_lb != null) {
      setEstimatedWeight(String(result.estimated_weight_lb));
    }
    if (result.source_price != null && !referencePrice) {
      setReferencePrice(String(result.source_price));
    }
    setPreviewMeta({
      source_price: result.source_price ?? null,
      landed_price: result.landed_price ?? null,
      pricing_model: result.pricing_model ?? null,
    });
  };

  const handleParseAndImport = async () => {
    const urlError = validateProductUrl(url);
    if (urlError) {
      toast.error(urlError);
      return;
    }

    setWorking(true);

    const preview = await previewProductUrl(url, API);
    if (preview.success) {
      applyPreview(preview);
    } else if (!title.trim()) {
      setWorking(false);
      toast.error(preview.error || "Could not parse URL — enter title and image manually");
      return;
    } else {
      toast.warning("Preview unavailable — submitting with manual details");
    }

    const images = imagesText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const resolvedTitle = (preview.success ? preview.title : title) || title;
    const resolvedImages = preview.success && preview.images?.length ? preview.images : images;

    const parsedReference =
      referencePrice.trim() !== ""
        ? Number.parseFloat(referencePrice)
        : preview.success
          ? preview.source_price ?? null
          : null;
    const parsedMargin =
      profitMarginOverride.trim() !== ""
        ? Number.parseFloat(profitMarginOverride)
        : null;

    const result = await submitProductUrlAsAdmin(
      {
        source_url: url,
        source_platform: platform,
        title: resolvedTitle || preview.title || "",
        images: resolvedImages.length ? resolvedImages : images,
        category,
        customer_email: customerEmail.trim() || null,
        estimated_weight_lb:
          estimatedWeight.trim() !== "" ? Number.parseFloat(estimatedWeight) : null,
        reference_price: Number.isFinite(parsedReference) ? parsedReference : null,
        profit_floor_override: Number.isFinite(parsedMargin) ? parsedMargin : null,
      },
      API
    );

    setWorking(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }

    const assigned = result.assignedCustomerEmail;
    toast.success(
      assigned
        ? `Product imported for ${assigned} — pending review`
        : "Product imported — pending review"
    );

    if (redirectOnSuccess) {
      navigate("/admin/submissions");
      return;
    }

    resetForm();
    onSubmitted?.(result);
  };

  const formBody = (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      <div>
        <Label htmlFor="admin-link-url">Product URL</Label>
        <Input
          id="admin-link-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.amazon.com/dp/... or Shein, Temu, AliExpress, Walmart"
          className="mt-1"
          data-testid="admin-submit-url-input"
        />
        {platform !== "other" && (
          <div className="mt-2">
            <MarketplaceBadge marketplace={platform} size="xs" />
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="admin-link-customer-email" className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            Assign to customer email{" "}
            <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Input
            id="admin-link-customer-email"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="customer@example.com"
            className="mt-1"
            data-testid="admin-submit-customer-email"
          />
        </div>
        <div>
          <Label htmlFor="admin-link-reference-price" className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            Supplier / override price (USD){" "}
            <span className="text-slate-400 font-normal">(optional)</span>
          </Label>
          <Input
            id="admin-link-reference-price"
            type="number"
            min="0"
            step="0.01"
            value={referencePrice}
            onChange={(e) => setReferencePrice(e.target.value)}
            placeholder="Auto-filled from scrape"
            className="mt-1"
            data-testid="admin-submit-reference-price"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="admin-link-margin-override">
          Custom margin / profit floor override (USD){" "}
          <span className="text-slate-400 font-normal">(optional)</span>
        </Label>
        <Input
          id="admin-link-margin-override"
          type="number"
          min="0"
          step="0.01"
          value={profitMarginOverride}
          onChange={(e) => setProfitMarginOverride(e.target.value)}
          placeholder="Manual concierge quote floor"
          className="mt-1"
          data-testid="admin-submit-margin-override"
        />
      </div>

      <div>
        <Label htmlFor="admin-link-title">Title</Label>
        <Input
          id="admin-link-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Filled after parse, or enter manually"
          className="mt-1"
          data-testid="admin-submit-title-input"
        />
      </div>

      <div>
        <Label htmlFor="admin-link-images">Image URLs (one per line)</Label>
        <Textarea
          id="admin-link-images"
          value={imagesText}
          onChange={(e) => setImagesText(e.target.value)}
          placeholder="https://..."
          rows={compact ? 2 : 3}
          className="mt-1"
          data-testid="admin-submit-images-input"
        />
      </div>

      {previewMeta && (previewMeta.source_price != null || previewMeta.landed_price != null) && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm">
          <p className="font-medium text-orange-900">Parsed pricing preview</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-orange-800">
            {previewMeta.source_price != null && (
              <span>Supplier cost: ${Number(previewMeta.source_price).toFixed(2)}</span>
            )}
            {previewMeta.landed_price != null && (
              <span>Landed price: ${Number(previewMeta.landed_price).toFixed(2)}</span>
            )}
          </div>
        </div>
      )}

      <Button
        type="button"
        className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white"
        onClick={handleParseAndImport}
        disabled={working || !url.trim()}
        data-testid="admin-parse-import-btn"
      >
        {working ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Parsing &amp; importing...
          </>
        ) : (
          <>
            <Link2 className="h-4 w-4 mr-2" />
            Parse &amp; Import Product
          </>
        )}
      </Button>
    </div>
  );

  if (compact) {
    return formBody;
  }

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Link2 className="h-5 w-5 text-orange-600" />
          Import Product Link
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Paste a marketplace URL to scrape product details and queue it for admin review under
          CheepSweepz.
        </p>
      </CardHeader>
      <CardContent>{formBody}</CardContent>
    </Card>
  );
}
